export type ProductGuideSelectionMode = 'SingleChoice' | 'MultipleChoice';

// Shopify collection option used anywhere the guide needs a reusable product set.
export interface ProductGuideCollectionOption {
    id: number;
    title: string;
    handle: string;
    collectionType: string;
}

// Compact row returned by the list endpoint.
export interface ProductGuideSummary {
    id: number;
    name: string;
    handle: string;
    description: string;
    guideType: string;
    isActive: boolean;
    requiresVehicleContext: boolean;
    bundleBuilderEnabled: boolean;
    discountEnabled: boolean;
    discountMinimumItems?: number | null;
    discountPercentage?: number | null;
    questionCount: number;
    resultGroupCount: number;
    createdAt: string;
    updatedAt?: string | null;
}

// Full editable guide shape used by create, update, and detail calls.
export interface ProductGuideDetail {
    id?: number | null;
    name: string;
    handle: string;
    description: string;
    guideType: string;
    isActive: boolean;
    requiresVehicleContext: boolean;
    bundleBuilderEnabled: boolean;
    discountEnabled: boolean;
    discountMinimumItems?: number | null;
    discountPercentage?: number | null;
    questions: ProductGuideQuestion[];
    resultGroups: ProductGuideResultGroup[];
}

// One shopper-facing wizard step.
export interface ProductGuideQuestion {
    id?: number | null;
    prompt: string;
    helpText: string;
    selectionMode: ProductGuideSelectionMode;
    isRequired: boolean;
    displayOrder: number;
    answers: ProductGuideAnswer[];
}

// One selectable option under a question.
export interface ProductGuideAnswer {
    id?: number | null;
    label: string;
    valueKey: string;
    helpText: string;
    isDefault: boolean;
    displayOrder: number;
    rules: ProductGuideAnswerRule[];
}

// Filtering effect contributed when this answer is selected.
export interface ProductGuideAnswerRule {
    id?: number | null;
    source: 'ShopifyCollection';
    effect: 'Require' | 'Exclude' | 'Prefer';
    shopifyCollectionId?: number | null;
    shopifyCollectionTitle: string;
    shopifyCollectionHandle: string;
    isActive: boolean;
    displayOrder: number;
}

// One package slot such as wheels, tires, or accessories.
export interface ProductGuideResultGroup {
    id?: number | null;
    title: string;
    slotKey: string;
    description: string;
    isRequired: boolean;
    minSelections: number;
    maxSelections: number;
    baseShopifyCollectionId?: number | null;
    baseShopifyCollectionTitle: string;
    baseShopifyCollectionHandle: string;
    noResultsBehavior: 'ShowEmpty' | 'HideGroup' | 'UseFallbackCollection';
    fallbackShopifyCollectionId?: number | null;
    fallbackShopifyCollectionTitle: string;
    fallbackShopifyCollectionHandle: string;
    displayOrder: number;
    filterScopes: ProductGuideResultGroupFilterScope[];
    products: ProductGuideResultProduct[];
}

// Defines which selected answer collection filters are relevant to a package slot.
export interface ProductGuideResultGroupFilterScope {
    id?: number | null;
    scopeType: 'ShopifyCollection' | 'VehicleFitment';
    shopifyCollectionId?: number | null;
    shopifyCollectionTitle: string;
    shopifyCollectionHandle: string;
    isActive: boolean;
}

// One candidate product or variant inside a package slot.
export interface ProductGuideResultProduct {
    id?: number | null;
    shopifyProductId: number;
    shopifyProductVariantId?: number | null;
    productTitle: string;
    variantTitle: string;
    sku: string;
    featuredImage: string;
    quantity: number;
    isDefaultSelected: boolean;
    displayOrder: number;
}

// Request shape used by the admin preview/storefront resolver.
export interface ProductGuideResolveRequest {
    selectedAnswerIds: number[];
    fieldValues: Record<string, string>;
}

// Resolver output grouped by package slot.
export interface ProductGuideResolveResponse {
    guideId: number;
    groups: ProductGuideResolvedGroup[];
}

// One resolved package slot with products plus explainability notes.
export interface ProductGuideResolvedGroup {
    resultGroupId: number;
    title: string;
    slotKey: string;
    hidden: boolean;
    emptyMessage: string;
    appliedFilters: string[];
    products: ProductGuideResolvedProduct[];
}

// Product row returned after the cumulative collection/rule resolver runs.
export interface ProductGuideResolvedProduct {
    shopifyProductId: number;
    productTitle: string;
    handle: string;
    vendor: string;
    productType: string;
    featuredImage: string;
    sku: string;
    price?: number | null;
}

// Starter guide used only by the "new guide" editor route.
export const createDefaultProductGuide = (): ProductGuideDetail => ({
    name: 'Wheel and tire guide',
    handle: 'wheel-and-tire',
    description: '',
    guideType: 'wheel-tire-package',
    isActive: false,
    requiresVehicleContext: true,
    bundleBuilderEnabled: true,
    discountEnabled: false,
    discountMinimumItems: null,
    discountPercentage: null,
    questions: [
        {
            prompt: 'What type of setup are you building?',
            helpText: '',
            selectionMode: 'SingleChoice',
            isRequired: true,
            displayOrder: 1,
            answers: [
                {
                    label: 'Daily driver',
                    valueKey: 'daily-driver',
                    helpText: '',
                    isDefault: false,
                    displayOrder: 1,
                    rules: [],
                },
                {
                    label: 'Trail focused',
                    valueKey: 'trail-focused',
                    helpText: '',
                    isDefault: false,
                    displayOrder: 2,
                    rules: [],
                },
            ],
        },
    ],
    resultGroups: [
        {
            title: 'Wheels',
            slotKey: 'wheels',
            description: '',
            isRequired: true,
            minSelections: 1,
            maxSelections: 1,
            baseShopifyCollectionId: null,
            baseShopifyCollectionTitle: '',
            baseShopifyCollectionHandle: '',
            noResultsBehavior: 'ShowEmpty',
            fallbackShopifyCollectionId: null,
            fallbackShopifyCollectionTitle: '',
            fallbackShopifyCollectionHandle: '',
            displayOrder: 1,
            filterScopes: [],
            products: [],
        },
        {
            title: 'Tires',
            slotKey: 'tires',
            description: '',
            isRequired: true,
            minSelections: 1,
            maxSelections: 1,
            baseShopifyCollectionId: null,
            baseShopifyCollectionTitle: '',
            baseShopifyCollectionHandle: '',
            noResultsBehavior: 'ShowEmpty',
            fallbackShopifyCollectionId: null,
            fallbackShopifyCollectionTitle: '',
            fallbackShopifyCollectionHandle: '',
            displayOrder: 2,
            filterScopes: [],
            products: [],
        },
    ],
});
